import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiGetResources, apiCreateResource, apiUpdateResource, apiDeleteResource, apiGetProjectSourceTree, apiUploadSource } from './api';
import { FileTreeView } from './FileTree';
import PageLoader, { MiniSpinner } from './PageLoader';
import { formatDate } from './dateUtils';

export default function SourceTreePage() {
  const { project, user } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [modal,      setModal]      = useState(null); // null | 'new' | resource obj
  const [filesModal, setFilesModal] = useState(null); // null | resource obj
  const [deleting,      setDeleting]      = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | resource obj
  const [page,          setPage]          = useState(1);
  const [statusFilter,  setStatusFilter]  = useState('all');

  const PAGE_SIZE = 10;

  useEffect(() => { load(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function load() {
    setLoading(true);
    setError('');
    apiGetResources(project.id)
      .then(setResources)
      .catch(() => setError('Failed to load resources.'))
      .finally(() => setLoading(false));
  }

  async function handleDelete(r) {
    setConfirmDelete(r);
  }

  async function confirmAndDelete(r) {
    setDeleting(r.resourceId);
    try {
      await apiDeleteResource(r.resourceId);
      setResources(prev => prev.filter(x => x.resourceId !== r.resourceId));
      setConfirmDelete(null);
    } catch {
      setError('Failed to delete resource.');
      setConfirmDelete(null);
    } finally {
      setDeleting(null);
    }
  }

  const activeCount   = resources.filter(r => r.isActive === 1).length;
  const inactiveCount = resources.filter(r => r.isActive !== 1).length;

  const filtered = resources.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      String(r.resourceId).includes(q) ||
      (r.resourceText  ?? '').toLowerCase().includes(q) ||
      (r.resourceNotes ?? '').toLowerCase().includes(q) ||
      (r.resourcePath  ?? '').toLowerCase().includes(q);
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active'   && r.isActive === 1) ||
      (statusFilter === 'inactive' && r.isActive !== 1);
    return matchSearch && matchStatus;
  });

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
  }

  function handleStatusFilter(key) {
    setStatusFilter(key);
    setPage(1);
  }

  return (
    <div className="customer-window">
      <div className="window-header">
        <div className="window-header-icon">🗂️</div>
        <div className="window-header-text">
          <h1>Resources</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: 0 }}>
            <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, letterSpacing: 0.2 }}>
              {'$Server'}
              <span style={{ opacity: 0.5 }}>\</span>
              {project.customerId ?? '—'}
              <span style={{ opacity: 0.5 }}>\</span>
              {project.id ?? '—'}
              <span style={{ opacity: 0.5 }}>\</span>
              {'Source'}
            </span>
            {!loading && (
              <span style={{ ...styles.countBadge, fontSize: 11 }}>
                {resources.length} item{resources.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button style={styles.addBtn} onClick={() => setModal('new')}>+ Add Resource</button>
        </div>
      </div>

      <div className="window-body">
        {error && (
          <div className="error-alert" style={{ marginBottom: 16 }}>{error}</div>
        )}

        {loading && <PageLoader label="Loading resources…" />}

        {!loading && (
          <>
            <div style={styles.toolbar}>
              <div style={styles.searchWrap}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  style={styles.searchInput}
                  type="text"
                  autoComplete="off"
                  placeholder="Search by text, notes or path…"
                  value={search}
                  onChange={handleSearch}
                />
                {search && (
                  <button style={styles.clearBtn} onClick={() => { setSearch(''); setPage(1); }}>✕</button>
                )}
              </div>
              <div style={styles.tabs}>
                {[
                  { key: 'all',      label: 'All',      count: resources.length },
                  { key: 'active',   label: 'Active',   count: activeCount },
                  { key: 'inactive', label: 'Inactive', count: inactiveCount },
                ].map(t => (
                  <button
                    key={t.key}
                    style={{ ...styles.tab, ...(statusFilter === t.key ? styles.tabActive : {}) }}
                    onClick={() => handleStatusFilter(t.key)}
                  >
                    {t.label}
                    <span style={{ ...styles.tabBadge, ...(statusFilter === t.key ? styles.tabBadgeActive : {}) }}>
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={styles.empty}>
                <span style={{ fontSize: 36 }}>{resources.length === 0 ? '📭' : '🔍'}</span>
                <p style={styles.emptyText}>
                  {resources.length === 0
                    ? 'No resources yet. Click "+ Add Resource" to create one.'
                    : `No resources match "${search}"`}
                </p>
              </div>
            ) : (
              <div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Resource Name</th>
                      <th style={styles.th}>Notes</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>Updated</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody key={currentPage}>
                    {paginated.map((r, i) => (
                      <tr
                        key={r.resourceId}
                        style={{
                          background:      i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-table-row-alt)',
                          cursor:          'pointer',
                          animation:       'row-in 0.32s ease both',
                          animationDelay:  `${i * 48}ms`,
                        }}
                        onDoubleClick={() => setModal(r)}
                      >
                        <td style={styles.td}>
                          <span style={styles.idBadge}>{r.resourceId}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.textCell}>{r.resourceText || <em style={{ color: 'var(--text-muted)' }}>—</em>}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.notesCell}>{r.resourceNotes || <em style={{ color: 'var(--text-muted)' }}>—</em>}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, ...(r.isActive === 1 ? styles.statusActive : styles.statusInactive) }}>
                            {r.isActive === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.dateCell}>{formatDate(r.createdat)}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.dateCell}>{formatDate(r.updatedat) || '—'}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <div style={styles.actionGroup}>
                            <button style={styles.editBtn} onClick={() => setModal(r)} title="Edit">✏️</button>
                            <button
                              style={styles.uploadBtn}
                              onClick={() => navigate(`/app/upload?resourceId=${r.resourceId}&folder=${encodeURIComponent(r.resourceText ?? '')}`)}
                              title="Upload"
                            >
                              ⬆️
                            </button>
                            <button
                              style={styles.filesBtn}
                              onClick={() => setFilesModal(r)}
                              title="View uploaded files"
                            >
                              📂
                            </button>
                            <button
                              style={styles.deleteBtn}
                              onClick={() => handleDelete(r)}
                              disabled={deleting === r.resourceId}
                              title="Delete"
                            >
                              {deleting === r.resourceId ? '…' : '🗑️'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      style={{ ...styles.pageBtn, ...(n === currentPage ? styles.pageBtnActive : {}) }}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                  <span style={styles.pageInfo}>
                    {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </span>
                </div>
              )}
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <ResourceModal
          resource={modal === 'new' ? null : modal}
          projectId={project.id}
          userId={user?.id ?? 0}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}

      {filesModal && (
        <FilesModal
          resource={filesModal}
          projectId={project.id}
          onClose={() => setFilesModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          resource={confirmDelete}
          loading={deleting === confirmDelete.resourceId}
          onCancel={() => !deleting && setConfirmDelete(null)}
          onConfirm={() => confirmAndDelete(confirmDelete)}
        />
      )}
    </div>
  );
}

function ConfirmDeleteModal({ resource, loading, onCancel, onConfirm }) {
  const name = resource.resourceText || `#${resource.resourceId}`;
  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={cdStyles.modal}>
        {/* Icon */}
        <div style={{ ...cdStyles.iconWrap, background: loading ? 'var(--bg-disabled)' : 'var(--error-bg)' }}>
          {loading
            ? <MiniSpinner size={28} color="var(--primary)" />
            : <span style={{ fontSize: 28 }}>🗑️</span>
          }
        </div>

        {/* Text */}
        <div style={cdStyles.body}>
          <h3 style={cdStyles.title}>{loading ? 'Deleting…' : 'Delete Resource'}</h3>
          <p style={cdStyles.message}>
            {loading ? (
              <span style={{ color: 'var(--text-muted)' }}>
                Removing <strong style={{ color: 'var(--text-primary)' }}>"{name}"</strong>, please wait…
              </span>
            ) : (
              <>
                Are you sure you want to delete{' '}
                <strong style={{ color: 'var(--text-primary)' }}>"{name}"</strong>?
                <br />
                <span style={cdStyles.warning}>This action cannot be undone.</span>
              </>
            )}
          </p>
        </div>

        {/* Actions */}
        <div style={cdStyles.footer}>
          <button style={{ ...cdStyles.cancelBtn, opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button style={{ ...cdStyles.deleteBtn, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} onClick={onConfirm} disabled={loading}>
            {loading
              ? <><MiniSpinner size={15} /> Deleting…</>
              : '🗑️ Delete'
            }
          </button>
        </div>
      </div>
    </div>
  );
}

const cdStyles = {
  modal: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
    width: '100%',
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 28px 24px',
    gap: 20,
    fontFamily: 'var(--font-family)',
    textAlign: 'center',
  },
  iconWrap: {
    width: 64, height: 64, borderRadius: '50%',
    background: 'var(--error-bg)',
    border: '2px solid rgba(192,57,43,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  body: { display: 'flex', flexDirection: 'column', gap: 8, width: '100%' },
  title: {
    fontSize: 'var(--font-size-lg)', fontWeight: 800,
    color: 'var(--text-primary)', margin: 0,
  },
  message: {
    fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)',
    lineHeight: 1.6, margin: 0,
  },
  warning: {
    fontSize: 'var(--font-size-sm)', color: 'var(--error)',
    fontWeight: 600,
  },
  footer: {
    display: 'flex', gap: 10, width: '100%',
  },
  cancelBtn: {
    flex: 1, padding: '10px 16px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', fontSize: 'var(--font-size-base)',
    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-family)',
  },
  deleteBtn: {
    flex: 1, padding: '10px 16px', borderRadius: 'var(--radius-sm)',
    border: 'none', background: 'var(--error)',
    color: '#fff', fontSize: 'var(--font-size-base)',
    fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-family)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
};

const FOLDER_INVALID_CHARS = /[\\/:*?"<>|]/;

function validateFolderName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'Resource Name is required.';
  if (trimmed !== value) return 'Folder name cannot have leading or trailing spaces.';
  if (FOLDER_INVALID_CHARS.test(trimmed)) return 'Folder name cannot contain: \\ / : * ? " < > |';
  if (trimmed === '.' || trimmed === '..') return 'Folder name cannot be "." or "..".';
  if (trimmed.length > 255) return 'Folder name cannot exceed 255 characters.';
  return null;
}

function ResourceModal({ resource, projectId, userId, onClose, onSave }) {
  const isEdit = !!resource;
  const fileInputRef = useRef(null);
  const [zipFile,       setZipFile]       = useState(null);
  const [resourceText,  setResourceText]  = useState(resource?.resourceText  ?? '');
  const [resourceNotes, setResourceNotes] = useState(resource?.resourceNotes ?? '');
  const [isActive,      setIsActive]      = useState(resource ? resource.isActive === 1 : true);
  const [saving,        setSaving]        = useState(false);
  const [saveStep,      setSaveStep]      = useState(''); // '' | 'creating' | 'uploading'
  const [err,           setErr]           = useState('');
  const [fieldErr,      setFieldErr]      = useState('');

  function handleZipChange(e) {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) return;
    setZipFile(picked);
    if (!isEdit && !resourceText) {
      const nameFromFile = picked.name.replace(/\.zip$/i, '');
      setResourceText(nameFromFile);
      setFieldErr(validateFolderName(nameFromFile) ?? '');
    }
  }

  function handleRemoveZip() {
    setZipFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleTextChange(e) {
    const val = e.target.value;
    setResourceText(val);
    setFieldErr(val ? (validateFolderName(val) ?? '') : '');
  }

  async function handleSave() {
    const validationErr = validateFolderName(resourceText);
    if (validationErr) { setFieldErr(validationErr); return; }
    if (!isEdit && !zipFile) { setErr('Please select a ZIP file to upload.'); return; }
    if (zipFile && !zipFile.name.toLowerCase().endsWith('.zip')) {
      setErr('Only .zip files are accepted.');
      return;
    }
    setSaving(true); setErr('');
    try {
      let resourceId;
      if (isEdit) {
        setSaveStep('creating');
        await apiUpdateResource(resource.resourceId, { resourceText, resourceNotes, isActive }, userId);
        resourceId = resource.resourceId;
      } else {
        setSaveStep('creating');
        const created = await apiCreateResource(projectId, { resourceText, resourceNotes }, userId);
        resourceId = created?.resourceId ?? created?.id;
      }
      if (zipFile && resourceId) {
        setSaveStep('uploading');
        await apiUploadSource(projectId, zipFile);
      }
      onSave();
    } catch (e) {
      setErr(e.message); setSaving(false); setSaveStep('');
    }
  }

  const savingLabel = saveStep === 'uploading' ? 'Uploading…' : saveStep === 'creating' ? 'Creating…' : 'Saving…';

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mStyles.modal}>
        <div style={mStyles.header}>
          <h2 style={mStyles.title}>{isEdit ? 'Edit Resource' : 'Add Resource'}</h2>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={mStyles.body}>
          {err && <div className="error-alert" style={{ marginBottom: 12 }}>{err}</div>}

          {/* Step 1: ZIP file */}
          <div style={mStyles.field}>
            <label style={mStyles.label}>ZIP File {!isEdit && <span style={{ color: 'var(--error)' }}>*</span>}</label>
            <div
              style={mStyles.dropZone}
              onClick={() => !saving && fileInputRef.current?.click()}
            >
              <span style={{ fontSize: 22 }}>🗜️</span>
              <span style={mStyles.dropLabel}>
                {zipFile ? zipFile.name : 'Click to select a .zip file'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              style={{ display: 'none' }}
              onChange={handleZipChange}
              disabled={saving}
            />
            {zipFile && (
              <div style={mStyles.fileRow}>
                <span>📦</span>
                <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{zipFile.name}</span>
                <button style={mStyles.removeFileBtn} onClick={handleRemoveZip} disabled={saving}>×</button>
              </div>
            )}
          </div>

          {/* Step 2: Name (auto-filled from zip) */}
          <div style={mStyles.field}>
            <label style={mStyles.label}>Resource Name * <span style={mStyles.hint}>(folder name)</span></label>
            <input
              style={{ ...mStyles.input, borderColor: fieldErr ? 'var(--error)' : undefined }}
              type="text"
              value={resourceText}
              onChange={handleTextChange}
              placeholder="Auto-filled from ZIP filename"
              disabled={saving || isEdit}
            />
            {fieldErr && <span style={mStyles.fieldErr}>{fieldErr}</span>}
            <span style={mStyles.helpText}>No spaces at start/end. Cannot contain: \ / : * ? " &lt; &gt; |</span>
          </div>

          {/* Notes (optional) */}
          <div style={mStyles.field}>
            <label style={mStyles.label}>Notes <span style={mStyles.hint}>(optional)</span></label>
            <textarea
              style={{ ...mStyles.input, resize: 'vertical', minHeight: 72 }}
              value={resourceNotes}
              onChange={e => setResourceNotes(e.target.value)}
              placeholder="Optional notes…"
              disabled={saving}
            />
          </div>

          {isEdit && (
            <div style={mStyles.field}>
              <label style={mStyles.label}>Status</label>
              <select style={mStyles.input} value={isActive ? 'active' : 'inactive'} onChange={e => setIsActive(e.target.value === 'active')} disabled={saving}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
        </div>
        <div style={mStyles.footer}>
          <button style={mStyles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={mStyles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? savingLabel : isEdit ? (zipFile ? 'Save & Upload' : 'Save Changes') : 'Create & Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilesModal({ resource, projectId, onClose }) {
  const [tree,    setTree]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    apiGetProjectSourceTree(projectId)
      .then(data => {
        if (Array.isArray(data)) {
          const found = data.find(n => n.resourceId === resource.resourceId);
          setTree(found?.tree ?? null);
        } else {
          setTree(data ?? null);
        }
      })
      .catch(() => setError('Failed to load uploaded files.'))
      .finally(() => setLoading(false));
  }, [projectId, resource.resourceId]);

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...mStyles.modal, maxWidth: 600 }}>
        <div style={mStyles.header}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <h2 style={mStyles.title}>Uploaded Files</h2>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>
              {resource.resourceText || `Resource #${resource.resourceId}`}
            </span>
          </div>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '16px 22px', minHeight: 200, maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && <PageLoader label="Loading files…" minHeight={200} />}

          {error && (
            <div className="error-alert">{error}</div>
          )}

          {!loading && !error && (
            <FileTreeView node={tree} resourceName={resource.resourceText} />
          )}
        </div>

        <div style={{ ...mStyles.footer, justifyContent: 'flex-end' }}>
          <button style={mStyles.cancelBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}


const styles = {
  countBadge: {
    background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.5)',
    color: '#fff', fontSize: 13, padding: '4px 14px', borderRadius: 20, fontWeight: 600,
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  addBtn: {
    background: '#fff', color: 'var(--primary)', border: 'none',
    borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer',
  },
  toolbar: { display: 'flex', gap: 12, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' },
  tabs: { display: 'flex', gap: 4 },
  tab: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)',
    fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
  },
  tabActive: {
    background: 'var(--info-bg)', border: '1px solid rgba(21,101,192,0.3)',
    color: 'var(--primary)', fontWeight: 700,
  },
  tabBadge: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 10, fontSize: 10, fontWeight: 700,
    padding: '1px 6px', color: 'var(--text-muted)',
  },
  tabBadgeActive: {
    background: 'rgba(21,101,192,0.15)', border: '1px solid rgba(21,101,192,0.25)',
    color: 'var(--primary)',
  },
  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: 420 },
  searchIcon: { position: 'absolute', left: 12, fontSize: 14, pointerEvents: 'none' },
  searchInput: {
    width: '100%', padding: '9px 36px 9px 36px',
    borderRadius: 8, border: '1.5px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-primary)',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'var(--font-family)',
  },
  clearBtn: {
    position: 'absolute', right: 10, background: 'none', border: 'none',
    cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 4,
  },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 8 },
  emptyText: { color: 'var(--text-secondary)', fontSize: 14, margin: 0, textAlign: 'center' },
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '11px 14px', textAlign: 'left',
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: 'var(--text-muted)', background: 'var(--bg-table-row-alt)',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  },
  td: { padding: '12px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  idBadge: {
    background: 'var(--info-bg)', color: 'var(--primary)',
    borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
  },
  textCell: { fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 },
  notesCell: { color: 'var(--text-secondary)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
  pathCell: {
    fontSize: 11, background: 'var(--bg-table-row-alt)', borderRadius: 4,
    padding: '2px 6px', color: 'var(--text-secondary)', maxWidth: 180,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
  },
  statusBadge: { borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive:   { background: 'var(--success-bg)', color: 'var(--success)' },
  statusInactive: { background: 'var(--error-bg)',   color: 'var(--error)'   },
  dateCell: { fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  actionGroup: { display: 'flex', gap: 6, justifyContent: 'flex-end' },
  editBtn: {
    background: 'var(--bg-table-row-alt)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1,
  },
  pagination: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 14, flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '6px 13px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-card)',
    color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  pageBtnActive: {
    background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)',
  },
  pageBtnDisabled: {
    opacity: 0.4, cursor: 'default',
  },
  pageInfo: {
    fontSize: 12, color: 'var(--text-muted)', marginLeft: 6, fontWeight: 500,
  },
  uploadBtn: {
    background: 'var(--info-bg)', border: '1px solid rgba(21,101,192,0.2)',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1,
  },
  filesBtn: {
    background: 'var(--success-bg)', border: '1px solid rgba(27,127,59,0.2)',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1,
  },
  deleteBtn: {
    background: 'var(--error-bg)', border: '1px solid rgba(211,47,47,0.2)',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1,
  },
};

const mStyles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)', width: '100%', maxWidth: 480,
    maxHeight: '90vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-family)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px 14px', borderBottom: '1px solid var(--border)',
  },
  title: { fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 },
  body: { padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  input: {
    padding: '9px 12px', borderRadius: 7, border: '1.5px solid var(--border)',
    background: 'var(--bg-page)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    fontFamily: 'var(--font-family)',
  },
  hint: { fontSize: 10, fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 },
  fieldErr: { fontSize: 11, color: 'var(--error)', fontWeight: 600, marginTop: 2 },
  helpText: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  dropZone: {
    border: '2px dashed var(--border)', borderRadius: 8, padding: '14px 16px',
    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
    background: 'var(--bg-table-row-alt)', marginTop: 4,
  },
  dropLabel: { fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '4px 8px', background: 'var(--bg-disabled)',
    borderRadius: 6, fontSize: 12, marginTop: 6,
  },
  removeFileBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-muted)', fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0,
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid var(--border)',
  },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', cursor: 'pointer',
  },
  saveBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
    border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer',
  },
};
